import { Eye, MousePointerClick, TrendingUp, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorCard } from "@/components/shared/ErrorCard";
import { useLeadStats } from "@/hooks/useLeadStats";

interface Props {
  businessId: string;
}

export const AnalyticsTab = ({ businessId }: Props) => {
  const { data: stats, isLoading, error, refetch } = useLeadStats(businessId);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-28 w-full rounded-card" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ErrorCard message={(error as Error).message} onRetry={() => refetch()} />
    );
  }

  const {
    totalViews = 0,
    roomClicks = 0,
    totalInteractions = 0,
    totalLeads = 0,
  } = stats ?? {};

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-card border border-border p-5">
        <h3 className="text-lg font-semibold mb-1">
          Business Views &amp; Clicks Overview
        </h3>
        <p className="text-sm text-muted-foreground mb-5">
          Real-time analytics on customer profile views, room clicks, and lead
          interactions in the app.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-muted/30 border border-border rounded-lg p-4 flex items-center gap-3">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full">
              <Eye className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalViews}</p>
              <p className="text-xs text-muted-foreground">
                Centre Profile Views
              </p>
            </div>
          </div>

          <div className="bg-muted/30 border border-border rounded-lg p-4 flex items-center gap-3">
            <div className="p-3 bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400 rounded-full">
              <MousePointerClick className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{roomClicks}</p>
              <p className="text-xs text-muted-foreground">
                Room Views &amp; Clicks
              </p>
            </div>
          </div>

          <div className="bg-muted/30 border border-border rounded-lg p-4 flex items-center gap-3">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-full">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalInteractions}</p>
              <p className="text-xs text-muted-foreground">
                Total Customer Interactions
              </p>
            </div>
          </div>

          <div className="bg-muted/30 border border-border rounded-lg p-4 flex items-center gap-3">
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-full">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalLeads}</p>
              <p className="text-xs text-muted-foreground">
                Total Unique Leads
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
